import { useState } from 'react';

import { fetchGetSellerPayLinkFeeTiers } from '@iwoca/lapi-client/iwocapay';
import { Modal } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';

import { useGetSharedFunctionality } from '../../../api/lending/lapiHooks';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { displayCurrency } from '../../../utils/DisplayCurrency';
import { ModalTable } from '../components/ModalTable';

const useSellerPayLinkFeeTiers = () => {
  const { stateKey } = useStateKey();

  const {
    data,
    isLoading,
    isFetching: isFetching,
  } = useQuery({
    queryKey: ['fetchGetSellerPayLinkFeeTiers'],
    queryFn: () =>
      fetchGetSellerPayLinkFeeTiers({
        stateKey: stateKey!,
      }),
    enabled: !!stateKey,
  });

  return {
    isLoading: isLoading || isFetching,
    volumePricingTiers: data?.data,
    totalProcessVolumeForMonth: data?.meta.current_amount,
  };
};

export const VolumePricingModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="large">
        <VolumePricingBreakdown />
      </Modal>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer bg-transparent px-xs py-0 text-s font-med text-primary-40 underline sm:p-0"
      >
        See price tiers
      </button>
    </>
  );
};

export const VolumePricingBreakdown = () => {
  const { functionality } = useGetSharedFunctionality({
    functionalities: ['is_30_days_enabled'],
  });
  const { volumePricingTiers, isLoading, totalProcessVolumeForMonth } =
    useSellerPayLinkFeeTiers();

  const loading = isLoading || !volumePricingTiers;

  return (
    <div>
      <h2 className="mb-s">Our pricing tiers</h2>
      <div className="mb-l">
        <div className="mb-l text-m font-reg">
          The more payments you take with Pay Later, the less you pay for them.
          Our lowest fees are 2.8% for 3 months and 13% for 12 months.
          {functionality?.is_30_days_enabled &&
            ' 30 days is always 1.8% regardless of monthly volumes.'}
        </div>
        <div className="text-m font-reg">
          Current month to date Pay Later transactions value:{' '}
          {loading ? (
            <Skeleton />
          ) : (
            <b>{displayCurrency(totalProcessVolumeForMonth || 0)}</b>
          )}
        </div>
      </div>

      <ModalTable
        isLoading={loading}
        isEmpty={false}
        tableHeaderRow={
          <tr>
            <th>Pay later / month</th>
            <th>Min</th>
            <th>Max</th>
            <th>3 month</th>
            <th>12 month</th>
          </tr>
        }
      >
        <>
          {volumePricingTiers?.map(({ lower_bound, upper_bound, products }) => (
            <tr key={`${lower_bound}${upper_bound}`}>
              <td>
                {displayCurrency(lower_bound / 1000, 0)}
                {upper_bound > 120000
                  ? '+'
                  : `-${displayCurrency(Math.trunc(upper_bound), 0).replace('£', '')}`}
              </td>
              <td>{displayCurrency(lower_bound, 0)}</td>
              <td>
                {upper_bound > 120000
                  ? ''
                  : displayCurrency(Math.trunc(upper_bound * 100) / 100)}
              </td>
              <td>{products[0].fee_percentage}%</td>
              <td>{products[1].fee_percentage}%</td>
            </tr>
          ))}
        </>
      </ModalTable>
    </div>
  );
};
